import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useSearchParams } from "../../../backend/navigation";
import { DatePickerBox, DropDown, PaginatedTable, SearchBar, Table, TitleBar } from '../../common';
import {
  PO_COLUMN,
  ROUTES,
  TITLES,
  POPUP_TYPE,
  BUTTON,
  BILL_GENERATION_COLUMN,
} from '../../../constants';
import { poDataCompiler, popup, toApiDate } from '../../../utils/';
import { vendorPurchase, createBill, getGroupBy } from '../../../service';
import { useDebounce } from '../../../hooks';
import { Button, PopUpBox, InputWithSearch } from '../../shared';
import { useNavigate } from "../../../backend/navigation";
import { getLocalStorageItem, setLocalStorageItem } from '../../../utils'
import { LOCAL_STORAGE, GROUP_BY } from '../../../constants';

const AVAILABLE_GROUP_VALUES_KEY = 'orders_available_group_values';


// Called inside effects: consume each deliberate event once, using its current closure.
// Reset on unmount also allows React StrictMode to replay mount setup safely.
const consumeListTrigger = (ref, key, next) => {
  const previous = ref.current[key];
  if (previous && next.every((value, i) => Object.is(value, previous[i]))) return false;
  ref.current[key] = next;
  return true;
};

export const PoContent = () => {
  // Only explicit list events issue queries; draft text and response metadata do not.
  const listTriggers = useRef({});
  useEffect(() => () => { listTriggers.current = {}; }, []);

  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params or default values
  const [poData, setPoData] = useState([]);
  const [billMode, setBillMode] = useState(0);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [search, setSearch] = useState(false);
  const [groupArray, setGroupArray] = useState([]);
  const [groupBy, setGroupBy] = useState(searchParams.get('groupBy') || GROUP_BY.TITLE);
  const [availableValuesGroupBy, setAvailableValuesGroupBy] = useState([]);
  const [selectedItem, setSelectedItem] = useState(
    searchParams.get('groupItems') ? searchParams.get('groupItems').split(',') : [],
  );
  const [groupApplied, setGroupApplied] = useState(searchParams.get('groupApplied') === 'true');
  const [sort1, setSort1] = useState(searchParams.get('filter1') || PO_COLUMN[7].name);
  const [sort2, setSort2] = useState(searchParams.get('filter2') || PO_COLUMN[8].name);
  const [sortingArray1, setSortingArray1] = useState([]);
  const [sortingArray2, setSortingArray2] = useState([]);
  const [filteredColumn, setFilteredColumn] = useState(PO_COLUMN);
  const [poList, setPoList] = useState([]);
  const [paginationData, setPaginationData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(parseInt(searchParams.get('page')) || 1);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isRestoringSession, setIsRestoringSession] = useState(true);
  const [selectionMode, setSelectionMode] = useState("page"); // "page" | "all"
  const [excludedIds, setExcludedIds] = useState(new Set());
  const [globalSelectAll, setGlobalSelectAll] = useState(false);
  const [selectableRecords, setSelectableRecords] = useState(null);

  const vendor_type = getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE);

  // Load cached available values from localStorage on component mount
  useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'restoreCachedGroups', [])) return;
    try {
      // Try to restore available values from localStorage
      const cachedValues = getLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY) || {};
      const currentGroupBy = searchParams.get('groupBy') || GROUP_BY.TITLE;

      if (cachedValues[currentGroupBy] && Array.isArray(cachedValues[currentGroupBy])) {
        setAvailableValuesGroupBy(cachedValues[currentGroupBy]);
      }
    } catch (error) {
      console.error('Error loading cached group values:', error);
    }
  }, [searchParams]);

  // Cache available values to localStorage whenever they change
  useEffect(() => {
    // Skip the initial setting when component first mounts
    if (!isRestoringSession && availableValuesGroupBy.length > 0) {
      try {
        const cachedValues = getLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY) || {};

        // Update the cached values for the current groupBy
        const updatedCache = {
          ...cachedValues,
          [groupBy]: availableValuesGroupBy,
        };

        setLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY, updatedCache);
      } catch (error) {
        console.error('Error caching group values:', error);
      }
    }

    // After first render, mark restoration as complete
    if (isRestoringSession) {
      setIsRestoringSession(false);
    }
  }, [availableValuesGroupBy, groupBy, isRestoringSession]);

  // Update URL params when values change
  const updateUrlParams = useCallback((params = {}) => {
    const newParams = {};

    // Only include non-empty values
    if (params.search) newParams.search = params.search;
    if (params.filter1 && params.filter1 !== PO_COLUMN[7].name) newParams.filter1 = params.filter1;
    if (params.filter2 && params.filter2 !== PO_COLUMN[8].name) newParams.filter2 = params.filter2;
    if (params.page) newParams.page = params.page.toString();
    if (params.groupBy && params.groupBy !== GROUP_BY.TITLE) newParams.groupBy = params.groupBy;

    // Fix for groupItems - extract only IDs before joining them in URL
    if (params.groupItems && params.groupItems.length > 0) {
      const ids = params.groupItems.map((item) => {
        // If item is an array [id, name], extract only the ID
        if (Array.isArray(item) && item.length > 1) {
          return item[0];
        }
        // If it's already a string ID or simple value, use as is
        return item;
      });
      newParams.groupItems = ids.join(',');
    }

    if (params.groupApplied === true) newParams.groupApplied = 'true';

    setSearchParams(newParams, { replace: true });
  }, [setSearchParams]);

  const fetchPO = useDebounce((params = {}) => {
    const updatedParams = { ...params };
    if (
      updatedParams.groupby_field === 'vendor_id,partner_id' &&
      updatedParams.groupby_specifics &&
      Object.keys(updatedParams.groupby_specifics).length > 0
    ) {
      const specifics = updatedParams.groupby_specifics;

      updatedParams.groupby_specifics = {
        vendor_id: specifics.vendor_id || [],
        selection: {
          partner_id: specifics.partner_id || [],
          vendor_id: false,
        },
      };
    }

    setIsLoading(true);
    vendorPurchase({ data: updatedParams }).then((response) => {
      setIsLoading(false);
      if (response?.data?.records?.length > 0) {
        setPoData(response.data.records);
        setPaginationData(response.data.params);
        setGroupArray(response?.data?.groupby || []);
        setSelectableRecords(response?.data?.selectable_records || 0)

        // Always update the sorting arrays with the latest data from the API
        const name1 = PO_COLUMN[7].dataField[0];
        const name2 = PO_COLUMN[8].dataField[0];

        if (response?.data[name1] && Array.isArray(response?.data[name1])) {
          setSortingArray1(response.data[name1]);
        }

        if (response?.data[name2] && Array.isArray(response?.data[name2])) {
          setSortingArray2(response.data[name2]);
        }
      } else {
        setPoData([]);
      }
    });
  }, 400);

  const fetchGroupBY = useDebounce((params = {}) => {
    getGroupBy({ data: params })
      .then((response) => {
        if (response?.data && response?.data[groupBy]) {
          setAvailableValuesGroupBy(response?.data[groupBy]);
        }
      })
      .catch((error) => {
        console.error('Error fetching group by options:', error);
        // If the API fails, try to use cached values
        try {
          const cachedValues = getLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY) || {};
          if (cachedValues[groupBy] && Array.isArray(cachedValues[groupBy])) {
            setAvailableValuesGroupBy(cachedValues[groupBy]);
          }
        } catch (err) {
          console.error('Error using backup cached values:', err);
        }
      })
  }, 400);

  // Helper function to refresh groupBy options with clean parameters when needed
  const refreshGroupByOptions = useCallback((includeFilters = true) => {
    if (initialLoadComplete && groupArray.includes(groupBy)) {
      let params = {
        related_to: GROUP_BY.ORDER,
        field_name: groupBy,
      };

      // Only include search and sort if requested
      if (includeFilters) {
        if (searchTerm) {
          params.search = {
            name: searchTerm,
            partner_ref: searchTerm,
            lot_name: searchTerm,
            container_no: searchTerm,
            invoice_number: searchTerm
          };
        }
        if (sort1 !== PO_COLUMN[7].name || sort2 !== PO_COLUMN[8].name) {
          let sortSelection = {};
          if (sort1 !== PO_COLUMN[7].name) {
            sortSelection.invoice_status = sort1.toLowerCase();
          }
          if (sort2 !== PO_COLUMN[8].name) {
            sortSelection.receipt_status = sort2.toLowerCase();
          }
          params.sort = { selection: sortSelection };
        }
      }
      fetchGroupBY(params);
    }
  }, [fetchGroupBY, groupArray, groupBy, initialLoadComplete, searchTerm, sort1, sort2]);

  const shouldApplyGroupBy = useCallback(() => {
    // Only apply groupBy if:
    // 1. groupApplied flag is true
    // 2. groupBy is a valid option in groupArray
    // 3. At least one item is selected OR we're just applying the groupBy field
    return (
      groupApplied &&
      ((groupArray.length > 0 && groupArray.includes(groupBy)) || initialLoadComplete === false) &&
      (selectedItem.length > 0 || !searchParams.get('groupItems'))
    );
  }, [groupApplied, groupArray, groupBy, initialLoadComplete, searchParams, selectedItem.length]);

  const getPoList = useCallback((additionalParams = {}) => {
    if (billMode === 2) return;

    setIsLoading(true);
    setGlobalSelectAll(false);
    setSelectionMode("page");

    const shouldUseGroupBy = shouldApplyGroupBy();

    let sortSelection = {};
    if (sort1 !== PO_COLUMN[7].name) sortSelection.invoice_status = sort1.toLowerCase();
    if (sort2 !== PO_COLUMN[8].name) sortSelection.receipt_status = sort2.toLowerCase();

    const params = {
      page_number: additionalParams.page_number || pageNumber,
      search: searchTerm
        ? {
          name: searchTerm,
          partner_ref: searchTerm,
          lot_name: searchTerm,
          container_no: searchTerm,
          invoice_number: searchTerm,
        }
        : {},
      sort: { selection: Object.keys(sortSelection).length > 0 ? sortSelection : {} },
      groupby_field: shouldUseGroupBy ? groupBy : '',
      ...additionalParams,
    };

    if (shouldUseGroupBy && selectedItem.length) {
      if (groupBy.includes(',')) {
        const fields = groupBy.split(',');
        const groupbySpecifics = {};
        fields.forEach((field) => {
          groupbySpecifics[field.trim()] = selectedItem.map((item) =>
            Array.isArray(item) && item.length > 1
              ? isNaN(Number(item[0])) ? item[0] : Number(item[0])
              : isNaN(Number(item)) ? item : Number(item)
          );
        });
        params.groupby_specifics = groupbySpecifics;
      } else {
        params.groupby_specifics = {
          [groupBy]: selectedItem.map((item) =>
            Array.isArray(item) && item.length > 1
              ? isNaN(Number(item[0])) ? item[0] : Number(item[0])
              : isNaN(Number(item)) ? item : Number(item)
          ),
        };
      }
    } else {
      params.groupby_specifics = {};
    }

    updateUrlParams({
      search: searchTerm,
      filter1: sort1 !== PO_COLUMN[7].name ? sort1 : '',
      filter2: sort2 !== PO_COLUMN[8].name ? sort2 : '',
      page: params.page_number,
      groupBy: shouldUseGroupBy ? groupBy : '',
      groupItems: shouldUseGroupBy && selectedItem.length ? selectedItem : [],
      groupApplied: shouldUseGroupBy,
    });

    fetchPO(params);
  }, [billMode, fetchPO, groupBy, pageNumber, searchTerm, selectedItem, shouldApplyGroupBy, sort1, sort2, updateUrlParams]);


  const removeGroupBy = () => {
    setGroupApplied(false);
    setSelectedItem([]);
    setAvailableValuesGroupBy([]);
    setGroupBy(GROUP_BY.TITLE);

    // Update URL - remove group params but keep existing sort filter
    updateUrlParams({
      search: searchTerm,
      filter1: sort1 !== PO_COLUMN[7].name ? sort1 : '',
      filter2: sort2 !== PO_COLUMN[8].name ? sort2 : '',
      page: 1,
      // Explicitly omit groupBy, groupItems, and groupApplied
    });

    // Fetch data with groupBy removed
    getPoList({
      page_number: 1,
      groupby_field: '',
      groupby_specifics: {},
    });
  };

  // Handle GroupBy selection change
  const handleGroupByChange = (newGroupBy) => {
    if (newGroupBy === groupBy) return;

    setGroupBy(newGroupBy);
    // When changing the groupBy field, reset selected items
    setSelectedItem([]);

    // Apply the groupBy automatically when it's a valid option
    if (groupArray.includes(newGroupBy)) {
      setGroupApplied(true);

      // Try to load cached values first for a smoother experience
      try {
        const cachedValues = getLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY) || {};
        if (cachedValues[newGroupBy] && Array.isArray(cachedValues[newGroupBy])) {
          setAvailableValuesGroupBy(cachedValues[newGroupBy]);
        }
      } catch (err) {
        console.error('Error using cached values for new groupBy:', err);
      }
    }
  };

  // Initial load - read from URL params
  useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'restoreInitialQuery', [])) return;
    // On initial load, if we have params in the URL, use them
    const urlSearchTerm = searchParams.get('search');
    const urlFilter1 = searchParams.get('filter1');
    const urlFilter2 = searchParams.get('filter2');
    const urlPage = searchParams.get('page');
    const urlGroupBy = searchParams.get('groupBy');
    const urlGroupItems = searchParams.get('groupItems');
    const urlGroupApplied = searchParams.get('groupApplied');

    let hasChanges = false;
    let hasGroupByParams = false;

    if (urlSearchTerm && urlSearchTerm !== searchTerm) {
      setSearchTerm(urlSearchTerm);
      hasChanges = true;
    }

    if (urlFilter1 && urlFilter1 !== sort1) {
      setSort1(urlFilter1);
      hasChanges = true;
    }

    if (urlFilter2 && urlFilter2 !== sort2) {
      setSort2(urlFilter2);
      hasChanges = true;
    }

    if (urlPage && parseInt(urlPage) !== pageNumber) {
      setPageNumber(parseInt(urlPage));
      hasChanges = true;
    }

    if (urlGroupBy && urlGroupBy !== groupBy) {
      setGroupBy(urlGroupBy);
      hasGroupByParams = true;
    }

    if (urlGroupItems) {
      const items = urlGroupItems.split(',');

      // Find the corresponding full items from availableValuesGroupBy if possible
      const fullItems = items.map((id) => {
        // Try to find a matching item in availableValuesGroupBy
        const match = availableValuesGroupBy.find(
          (item) => Array.isArray(item) && item[0].toString() === id.toString(),
        );

        // If found, return the full [id, name] array, otherwise return just the id
        return match || id;
      });

      if (JSON.stringify(fullItems) !== JSON.stringify(selectedItem)) {
        setSelectedItem(fullItems);
        hasGroupByParams = true;
      }
    }

    if (urlGroupApplied === 'true' && !groupApplied) {
      setGroupApplied(true);
      hasGroupByParams = true;
    }

    // Check if we have cached values for the current groupBy
    const cachedValues = getLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY) || {};
    const hasCachedValues =
      urlGroupBy && cachedValues[urlGroupBy] && Array.isArray(cachedValues[urlGroupBy]);

    // If we have cached values, use them for a faster initial render
    if (hasCachedValues) {
      setAvailableValuesGroupBy(cachedValues[urlGroupBy]);
    }

    // Check if we need to fetch group options
    if (hasGroupByParams && urlGroupBy) {
      // Pre-fetch the group options if needed
      let sortSelection = {};
      if (urlFilter1 && urlFilter1 !== PO_COLUMN[7].name) {
        sortSelection.invoice_status = urlFilter1.toLowerCase();
      }
      if (urlFilter2 && urlFilter2 !== PO_COLUMN[8].name) {
        sortSelection.receipt_status = urlFilter2.toLowerCase();
      }

      const params = {
        related_to: GROUP_BY.ORDER,
        field_name: urlGroupBy,
        search: urlSearchTerm
          ? {
            name: urlSearchTerm,
            partner_ref: urlSearchTerm,
            lot_name: urlSearchTerm,
          }
          : {},
        sort: Object.keys(sortSelection).length > 0 ? { selection: sortSelection } : {},
      };

      // Fetch group by options first
      fetchGroupBY(params);
    }

    // Always fetch data on initial load to ensure all filters are applied
    if (!hasChanges) {
      getPoList();
    }

    // Mark initial load as complete
    setInitialLoadComplete(true);
  }, [availableValuesGroupBy, fetchGroupBY, getPoList, groupApplied, groupBy, pageNumber, searchParams, searchTerm, selectedItem, sort1, sort2]);

  // Handle selected items change for groupBy
  useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'selectedItem', [selectedItem])) return;
    // Skip initial render
    if (initialLoadComplete) {
      // Apply groupBy automatically when items are selected
      setGroupApplied(true);

      // Reset to page 1 only when first applying the groupBy
      // (when there were no items before but now there are)
      const prevItems = searchParams.get('groupItems');
      const wasEmpty = !prevItems || prevItems.split(',').length === 0;

      if (wasEmpty) {
        setPageNumber(1);
        getPoList({ page_number: 1 });
      } else {
        // Keep the current page number for subsequent selections
        getPoList({ page_number: pageNumber });
      }
    }
  }, [selectedItem, getPoList, initialLoadComplete, pageNumber, searchParams]);

  // Handle search functionality
  useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'search', [search])) return;
    if (search) {
      setPageNumber(1);
      getPoList({ page_number: 1 });

      // Also refresh the group by options if needed
      if (initialLoadComplete && groupArray.includes(groupBy)) {
        refreshGroupByOptions(true); // Include filters since we're adding a search
      }
    }
  }, [search, getPoList, groupArray, groupBy, initialLoadComplete, refreshGroupByOptions]);

  // Handle page number changes
  useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'pageNumber', [pageNumber])) return;
    // Only fetch when pageNumber changes and it's not the initial load
    const currentUrlPage = searchParams.get('page');
    if (initialLoadComplete && currentUrlPage !== pageNumber.toString() && pageNumber !== 0) {
      getPoList();
    }
  }, [pageNumber, getPoList, initialLoadComplete, searchParams]);

  // Handle search term changes
  useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'searchTerm', [searchTerm])) return;
    if (initialLoadComplete && !searchTerm && search) {
      setPageNumber(1);
      setSearch(false);
      getPoList({ page_number: 1 });

      // If search is cleared and groupBy is applied, refresh the groupBy options
      // without any search filter
      if (groupArray.includes(groupBy)) {
        refreshGroupByOptions(false); // Don't include filters when search is removed
      }
    }
  }, [searchTerm, getPoList, groupArray, groupBy, initialLoadComplete, refreshGroupByOptions, search]);

  // Handle sorting changes
  useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'sort1_sort2', [sort1, sort2])) return;
    // Skip initial render
    if (initialLoadComplete) {
      // Check if sort1 or sort2 have changed from their initial values
      const initialSort1 = searchParams.get('filter1') || PO_COLUMN[7].name;
      const initialSort2 = searchParams.get('filter2') || PO_COLUMN[8].name;

      // Only trigger a new API call if the values have actually changed
      if (sort1 !== initialSort1 || sort2 !== initialSort2) {
        setPageNumber(1);
        getPoList({ page_number: 1 });

        // Also refresh the group by options if needed
        if (groupArray.includes(groupBy)) {
          // Check if we're removing filters (going back to defaults)
          const includeFilters = sort1 !== PO_COLUMN[7].name || sort2 !== PO_COLUMN[8].name;
          refreshGroupByOptions(includeFilters);
        }
      }
    }
  }, [sort1, sort2, getPoList, groupArray, groupBy, initialLoadComplete, refreshGroupByOptions, searchParams]);

  // Fetch group by values when groupBy changes
  useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'groupBy', [groupBy])) return;
    if (initialLoadComplete && groupArray.includes(groupBy)) {
      // When changing the groupBy field, we want to show all options
      // regardless of current filters
      refreshGroupByOptions(false); // Don't include filters for initial groupBy options
    }
  }, [groupBy, groupArray, initialLoadComplete, refreshGroupByOptions]);

  // Apply groupBy when groupApplied changes
  useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'groupApplied', [groupApplied])) return;
    if (initialLoadComplete) {
      // Keep the current page number when groupApplied changes
      getPoList({ page_number: 1 });
    }
  }, [groupApplied, getPoList, initialLoadComplete]);

  // Reset filter
  const resetFilter = () => {
    setSort1(PO_COLUMN[7].name);
    setSort2(PO_COLUMN[8].name);
    setPageNumber(1);

    // Remove filters from URL
    updateUrlParams({
      search: searchTerm,
      page: '1',
      groupBy: groupApplied ? groupBy : '',
      groupItems: groupApplied && selectedItem.length ? selectedItem : [],
      groupApplied: groupApplied,
    });

    getPoList({ page_number: 1 });

    // Refresh groupBy options without filters when filters are cleared
    if (groupArray.includes(groupBy)) {
      refreshGroupByOptions(false);
    }
  };

  // Updated addPOToBill function to handle both old logic and new select all logic
  const addPOToBill = ({ id, bill_amount, name, currency_code }) => {
    if (selectionMode === "all") {
      const next = new Set(excludedIds);
      if (excludedIds.has(id)) {
        next.delete(id); // re-check
      } else {
        next.add(id);    // uncheck
      }
      setExcludedIds(next);
    } else {
      if (poList.some((po) => po.id === id)) {
        setPoList((prev) => prev.filter((po) => po.id !== id));
      } else {
        const poInfo = poData.find((po) => po.id === id) || {};
        const [compiledData] = poDataCompiler([poInfo]) || [];

        const finalName = name || compiledData?.name || '';
        const finalCurrency = currency_code || compiledData?.currency_code || '';
        const finalAmount = bill_amount ?? compiledData?.bill_amount ?? 0;

        setPoList((prev) => [
          { id, name: finalName, currency_code: finalCurrency, amount: finalAmount },
          ...prev,
        ]);
      }
    }
  };

  // Updated checked function to handle both old logic and new select all logic
  const checked = (id, invoice_status) => {
    if (selectionMode === "all") {
      return !excludedIds.has(id) && invoice_status === 'To invoice';
    }
    return poList.map((po) => po.id).includes(id);
  };

  const handleAddBill = () => {
    if (billMode) { setBillMode(0); setPoList([]); setSelectionMode('page'); setGlobalSelectAll(false); setExcludedIds(new Set()); }
    else setBillMode(1);
  };

  const handleSelectAllVisible = () => {
    const visibleAssignedIds = poData.filter(row => row.invoice_status === 'to invoice').map(row => row.id);
    visibleAssignedIds.forEach(id => {
      if (!checked(id)) {
        addPOToBill({ id });
      }
    });
    setSelectionMode("page");
  };

  const handleSelectAllMatching = () => {
    setSelectionMode("all");
    setExcludedIds(new Set());
    setGlobalSelectAll(true);
    setPoList([]); // just clear visible page, don’t call API
  };

  const fetchAllPOsForBill = async () => {
    try {
      setIsLoading(true);
      const shouldUseGroupBy = shouldApplyGroupBy();

      // Create a sort object considering all filter scenarios
      let sortSelection = {};

      // Check if sort1 is selected (not default) and should be applied
      if (sort1 !== PO_COLUMN[7].name) {
        sortSelection.invoice_status = sort1.toLowerCase();
      }

      // Check if sort2 is selected (not default) and should be applied
      if (sort2 !== PO_COLUMN[8].name) {
        sortSelection.receipt_status = sort2.toLowerCase();
      }

      const params = {
        select_all: true, // This tells the API to return all matching records
        search: searchTerm
          ? {
            name: searchTerm,
            partner_ref: searchTerm,
            lot_name: searchTerm,
            container_no: searchTerm,
            invoice_number: searchTerm
          }
          : {},
        sort: {
          selection: Object.keys(sortSelection).length > 0 ? sortSelection : {},
        },
        groupby_field: shouldUseGroupBy ? groupBy : '',
        excluded_ids: Array.from(excludedIds), // Pass excluded IDs for "all" mode
        page_number: 1,
      };

      if (shouldUseGroupBy && selectedItem.length) {
        if (groupBy.includes(',')) {
          // For comma-separated fields, create an object with each field as a key
          const fields = groupBy.split(',');
          const groupbySpecifics = {};

          fields.forEach((field) => {
            groupbySpecifics[field.trim()] = selectedItem.map((item) => {
              // If item is an array [id, name], extract only the ID
              if (Array.isArray(item) && item.length > 1) {
                // Convert ID to number if possible
                const id = item[0];
                return !isNaN(Number(id)) ? Number(id) : id;
              }
              // If it's already a value, try to convert to number if possible
              return !isNaN(Number(item)) ? Number(item) : item;
            });
          });

          params.groupby_specifics = groupbySpecifics;
        } else {
          // For single field, use the original format but ensure we extract IDs as numbers
          params.groupby_specifics = {
            [groupBy]: selectedItem.map((item) => {
              // If item is an array [id, name], extract only the ID
              if (Array.isArray(item) && item.length > 1) {
                // Convert ID to number if possible
                const id = item[0];
                return !isNaN(Number(id)) ? Number(id) : id;
              }
              // If it's already a value, try to convert to number if possible
              return !isNaN(Number(item)) ? Number(item) : item;
            }),
          };
        }
      } else {
        params.groupby_specifics = {};
      }

      const response = await vendorPurchase({ data: params });

      if (response?.data?.records?.length > 0) {
        // Filter only the records with invoice_status === 'to invoice' and not excluded
        const eligibleRecords = response.data.records.filter(
          record => record.invoice_status === 'to invoice' && !excludedIds.has(record.id)
        );

        // Use poDataCompiler to sanitize the data
        const compiledData = poDataCompiler(eligibleRecords) || [];

        // Transform to the format expected by GenerateBill
        const transformedPOList = compiledData.map((po) => ({
          id: po.id,
          name: po.name || '',
          currency_code: po.currency_code || '',
          amount: po.bill_amount ?? 0,
        }));

        return transformedPOList;
      } else {
        return [];
      }
    } catch (error) {
      console.error('Error fetching all POs for bill:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // Updated handleGenerateBill function
  const handleGenerateBill = async () => {
    let finalPOList = [];

    if (selectionMode === "all") {
      // Fetch all matching POs from API
      setBillMode(2);
    } else {
      // Use the existing poList for "page" mode
      finalPOList = [...poList];
      if (finalPOList.length > 0) {
        setBillMode(2);
      } else {
        popup(POPUP_TYPE.ERR, 'No eligible POs found for bill generation');
      }
    }
  };

  useEffect(() => {
    const vendorType = getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE);
    let columns = billMode ? PO_COLUMN : PO_COLUMN.filter((col) => col.name !== '*');
    columns = columns.filter((col) => {
      if (col.name === 'Container No' && (vendorType === 'fabric' || vendorType === 'dyeing')) {
        return false; // Hide Container No if vendor is fabric or dyeing
      }
      return true;
    });
    setFilteredColumn(columns);
  }, [billMode]);

  return (
    <>
      <TitleBar title={[{ text: TITLES.PURCHASE_ORDER, route: ROUTES.PURCHASE_ORDER }]} />
      <div className="d-flex flex-wrap align-items-center justify-content-start gap-3 w-100">
        <SearchBar
          search={search}
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm}
          setSearch={setSearch}
          onDefaultSearch={() => getPoList({ page_number: 1 })}
        />
        <div className="mb-3 filter-btns-div d-flex align-items-center justify-content-start">
          <DropDown dropdownMenu={sortingArray1} value={sort1} setValue={setSort1}></DropDown>
          <DropDown dropdownMenu={sortingArray2} value={sort2} setValue={setSort2}></DropDown>
          {(sortingArray2.includes(sort2) || sortingArray1.includes(sort1)) && (
            <Button
              clickHandler={resetFilter}
              className="blue-btn d-flex align-items-center justify-content-center px-3">
              {BUTTON.CLEAR_FILTERS}
            </Button>
          )}
        </div>
      </div>

      {/* Group By Controls */}
      <div className="d-block align-items-center justify-content-start gap-3 w-100">
        <div className="w-50 mb-3 filter-btns-div d-flex align-items-center justify-content-start">
          <DropDown dropdownMenu={groupArray} value={groupBy} setValue={handleGroupByChange} />
          {groupArray.includes(groupBy) && (
            <InputWithSearch
              setAvailableItem={setAvailableValuesGroupBy}
              availableItem={availableValuesGroupBy}
              selectedItem={selectedItem}
              setSelectedItem={setSelectedItem}
            />
          )}
          {groupArray.includes(groupBy) && (
            <Button
              clickHandler={removeGroupBy}
              className="blue-btn d-flex align-items-center justify-content-center px-3">
              {BUTTON.REMOVE}
            </Button>
          )}
        </div>
      </div>

      {/* Updated Records Selected + Action Buttons section - same as Bills and Receipt */}
      {Array.isArray(poData) && poData?.length > 0 && (
        <div className="d-flex justify-content-between align-items-center mb-3 w-100">
          {/* Left: Buttons + Selected count */}
          <div className="d-flex align-items-center gap-3">
            {vendor_type !== 'clothing' && (
              <Button
                clickHandler={handleAddBill}
                className="blue-btn d-flex align-items-center justify-content-center px-3"
              >
                {billMode ? BUTTON.CANCEL : BUTTON.CREATE_BILL}
              </Button>
            )}

            {(billMode && !globalSelectAll) ? (
              <>
                <Button
                  clickHandler={handleSelectAllVisible}
                  className="blue-btn d-flex align-items-center justify-content-center px-3">
                  Select Page
                </Button>
              </>
            ) : ''}

            {(poList.length > 0 && !globalSelectAll) && (
              <>
                <span className="text-muted ms-2">
                  {poList.length} {poList.length === 1 ? 'record' : 'records'} selected
                </span>
                {!(poList.length === selectableRecords) && <span
                  onClick={handleSelectAllMatching}
                  className="text-primary cursor-pointer fw-semibold"
                  role="button">
                  Select All ({selectableRecords})
                </span>}
              </>
            )}
            {selectionMode === 'all' && (
              <>
                <span className="text-muted ms-2">
                  {selectableRecords - excludedIds.size}{' '}
                  {(selectableRecords - excludedIds.size) === 1 ? 'record' : 'records'} selected
                </span>
                {!(poList.length === paginationData?.total_records) && excludedIds.size > 0 && <span
                  onClick={handleSelectAllMatching}
                  className="text-primary cursor-pointer fw-semibold"
                  role="button">
                  Select All ({selectableRecords})
                </span>}
              </>
            )}
          </div>

          {/* Right: Generate Bill button */}
          <div className="d-flex">
            {(poList.length > 0 || selectionMode === 'all') && (
              <Button
                clickHandler={handleGenerateBill}
                className="blue-btn d-flex align-items-center justify-content-center px-3">
                {BUTTON.GENERATE_BILL}
              </Button>
            )}
          </div>
        </div>
      )}

      {Array.isArray(poData) ? (
        <PaginatedTable
          columns={filteredColumn}
          initialData={poData}
          colFilter={true}
          to={billMode ? '' : `${ROUTES.PURCHASE_ORDER}`}
          compiler={{ use: true, tool: poDataCompiler }}
          func={{ addPOToBill, checked }}
          className="table-4"
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          paginationData={paginationData}
          isLoading={isLoading}
        />
      ) : (
        <div>
          <h4 className="text-center px-1 py-5">No Records Found</h4>
        </div>
      )}
      <PopUpBox openPopUpBox={billMode === 2}>
        <GenerateBill
          poList={selectionMode === "all" ? [] : poList}
          allPOsData={selectionMode === "all" ? {
            searchTerm,
            sort1,
            sort2,
            groupBy: shouldApplyGroupBy() ? groupBy : '',
            selectedItem: shouldApplyGroupBy() && selectedItem.length ? selectedItem : [],
            excludedIds
          } : null}
          setPoList={setPoList}
          setBillMode={setBillMode}
          billMode={billMode}
          fetchAllPOsForBill={selectionMode === "all" ? fetchAllPOsForBill : null}
          setSelectionMode={setSelectionMode}
          setGlobalSelectAll={setGlobalSelectAll}
        />
      </PopUpBox>
    </>
  );
};

export const GenerateBill = ({ poList, allPOsData, setPoList, setBillMode, fetchAllPOsForBill, billMode, setSelectionMode, setGlobalSelectAll }) => {
  // Only explicit list events issue queries; draft text and response metadata do not.
  const listTriggers = useRef({});
  useEffect(() => () => { listTriggers.current = {}; }, []);

  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const [isBillCreationLoading, setIsBillCreationLoading] = useState(false);
  const [finalPOList, setFinalPOList] = useState([]);
  const [isLoadingAllPOs, setIsLoadingAllPOs] = useState(false);

  useEffect(() => {
    if (!consumeListTrigger(listTriggers, 'billMode', [billMode])) return;
    if (billMode !== 2) return;

    const loadPOData = async () => {
      if (allPOsData && fetchAllPOsForBill) {
        setIsLoadingAllPOs(true);
        try {
          const allPOs = await fetchAllPOsForBill();
          setFinalPOList(allPOs);
        } catch (error) {
          console.error('Error loading all POs:', error);
          setFinalPOList([]);
        } finally {
          setIsLoadingAllPOs(false);
        }
      } else {
        setFinalPOList(poList || []);
      }
    };

    loadPOData();
  }, [billMode, allPOsData, fetchAllPOsForBill, poList]);

  const handleClose = () => {
    setPoList([]);
    setBillMode(0);
    setDate(new Date());
    setFinalPOList([]);
    setSelectionMode("page");
    setGlobalSelectAll(false);
  };

  useEffect(() => {
    if (finalPOList.length === 0 && !isLoadingAllPOs) {
      setBillMode(0);
      setDate(new Date());
    }
  }, [finalPOList, isLoadingAllPOs, setBillMode]);

  const handleDelete = (id) => {
    setFinalPOList((prev) => {
      // Find the PO with the given id
      const target = prev.find((po) => po.id === id);
      if (!target) return prev;

      // Remove all records with the same name
      return prev.filter((po) => po.name !== target.name);
    });

    if (!allPOsData) {
      setPoList((prev) => {
        const target = prev.find((po) => po.id === id);
        if (!target) return prev;

        return prev.filter((po) => po.name !== target.name);
      });
    }
  };

  const groupByName = (list) => {
    const grouped = {};
    list.forEach((po) => {
      if (!grouped[po.name]) {
        grouped[po.name] = { ...po };
      } else {
        grouped[po.name].amount += po.amount;
      }
    });
    return Object.values(grouped);
  };

  const handleBillCreation = () => {
    setIsBillCreationLoading(true);

    createBill({
      data: {
        purchase_id: finalPOList.map((po) => po.id),
        bill_date: toApiDate(date)
      },
    })
      .then((response) => {
        const createdBills = response?.data || [];

        if (createdBills.length > 0) {
          const billData = createdBills[0];

          popup(
            POPUP_TYPE.SUCCESS,
            response?.message || "Bill(s) created successfully."
          );

          setIsBillCreationLoading(false);

          setTimeout(() => {
            navigate(`${ROUTES.BILLS}/${billData.name.replaceAll("/", "_")}`, {
              state: billData,
            });
          }, 100);

          return;
        }

        // If no bills created, show error
        setIsBillCreationLoading(false);
      })
      .catch((error) => {
        console.error("Error while creating bill:", error);
        setIsBillCreationLoading(false);
      });
  };

  if (isLoadingAllPOs) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <span className="ms-3">Loading PO data...</span>
      </div>
    );
  }

  return (
    <>
      <div style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: "8px", minHeight: "400px" }}>
        {/* Sticky Header */}
        <div
          style={{
            position: "sticky",
            top: 0,
            background: "white",
            zIndex: 10,
            padding: "8px 0",
          }}
        >
          <h4 className="d-flex justify-content-center m-2 align-items-center">
            Generate Bill
          </h4>
          <hr className="m-2" />
        </div>

        {/* Sticky Date Picker */}
        <div
          className="d-flex justify-content-start align-items-center gap-2 mb-2"
          style={{
            position: "sticky",
            top: "60px", // adjust based on header height
            background: "white",
            zIndex: 9,
            padding: "8px 0",
          }}
        >
          <h6 className="d-flex px-3 mt-2">Date : </h6>
          <DatePickerBox
            value={date}
            setValue={setDate}
            clickHandler={() => { }}
            showSave={"hide"}
          />
        </div>

        {/* Table Section */}
        <div>
          {Array.isArray(finalPOList) && finalPOList.length > 0 && (
            <Table
              columns={BILL_GENERATION_COLUMN}
              initialData={groupByName(finalPOList).reverse()}
              func={handleDelete}
            />
          )}
        </div>
        <hr />
      </div>

      {/* Footer stays fixed at bottom */}
      <div className="d-flex justify-content-between align-items-center mt-2">
        <h6 className="d-flex px-3 ">
          {` Total- ${finalPOList
            .reduce((acc, cum) => (acc += cum.amount), 0)
            .toFixed(2)} ${finalPOList[0]?.currency_code || ''}`}
        </h6>
        <div className="d-flex gap-2">
          <Button
            clickHandler={handleBillCreation}
            disabled={!date || isBillCreationLoading || finalPOList.length === 0}
            className={`blue-btn ${!isBillCreationLoading ? "" : "disabled"
              } d-flex align-items-center justify-content-center px-3`}
          >
            {isBillCreationLoading && (
              <span
                className="spinner-border spinner-border-sm text-white me-2"
                role="status"
              />
            )}
            {BUTTON.GENERATE_BILL}
          </Button>

          <Button
            clickHandler={handleClose}
            className="blue-btn d-flex align-items-center justify-content-center px-3"
          >
            {BUTTON.CANCEL}
          </Button>
        </div>
      </div>
    </>
  );
};
